import React, {useEffect, useRef, useState} from 'react';
import {useSelector} from 'react-redux';
import styled from 'styled-components';
import classNames from 'classnames';
import MotifToast from '@ey/motif-react-wrapper/Toast';
import {useEngagement} from '../../../util/customHooks';
import {labels} from '../../../util/utils';
import ResourceWithLink from '../../Common/ResourceWithLink/ResourceWithLink';
import {isWithinFiveWorkingDaysOfRRD, shouldShowReminder, recordReminderShown, formatReminderMessage} from './helpers';

export default function FourFortyRASReminder({className}) {
	const engagement = useEngagement();
	const hasModificationsAfterRRD = useSelector((state) => state.quickLink?.hasModificationsAfterRRD);

	const [isOpen, setIsOpen] = useState(false);
	const timerRef = useRef();
	const closeTimeout = 30000; // 30 Seconds

	// Reference to track the delay timer
	const delayTimerRef = useRef();
	const displayDelayMs = 5000; // 5 second delay before showing the toast

	useEffect(() => {
		// Clear any existing timers when dependencies change
		if (timerRef.current) {
			clearTimeout(timerRef.current);
			timerRef.current = null;
		}

		if (delayTimerRef.current) {
			clearTimeout(delayTimerRef.current);
			delayTimerRef.current = null;
		}

		if (!engagement?.expectedReportReleaseDate || !hasModificationsAfterRRD) {
			return;
		}

		if (!isWithinFiveWorkingDaysOfRRD(engagement)) {
			return;
		}

		if (shouldShowReminder(engagement)) {
			// Delay showing the toast to allow the app to load properly
			delayTimerRef.current = setTimeout(() => {
				setIsOpen(true);

				// Only start the auto-close timer after the toast is displayed
				timerRef.current = setTimeout(() => {
					handleOnMessageClose();
				}, closeTimeout);
			}, displayDelayMs);
		}

		return () => {
			// Clean up both timers on unmount or dependency change
			if (timerRef.current) {
				clearTimeout(timerRef.current);
			}

			if (delayTimerRef.current) {
				clearTimeout(delayTimerRef.current);
			}
		};
	}, [engagement, hasModificationsAfterRRD, closeTimeout, displayDelayMs]);

	const isWithinFiveWorkingDays = isWithinFiveWorkingDaysOfRRD(engagement);
	const reminderTitle = labels.rrdReminderTitle;
	const messageTemplate = formatReminderMessage(engagement, labels);

	const reminderFinalMessage = <ResourceWithLink className="four-forty-reminder-message" placeholder={messageTemplate} onPlaceholderClickConfig={{}} />;

	const handleOnMessageClose = () => {
		setIsOpen(false);

		// Clear both timers when manually closed
		if (timerRef.current) {
			clearTimeout(timerRef.current);
			timerRef.current = null;
		}

		if (delayTimerRef.current) {
			clearTimeout(delayTimerRef.current);
			delayTimerRef.current = null;
		}

		recordReminderShown(engagement);
	};

	return (
		<React.Fragment>
			{engagement?.expectedReportReleaseDate && hasModificationsAfterRRD && isWithinFiveWorkingDays && isOpen && (
				<StyledFourFortyRASReminder className={classNames('StyledFourFortyRASReminder', {className})}>
					<MotifToast variant="warning" supportingText={reminderFinalMessage} onClose={handleOnMessageClose}>
						{reminderTitle}
					</MotifToast>
				</StyledFourFortyRASReminder>
			)}
		</React.Fragment>
	);
}

const StyledFourFortyRASReminder = styled.section`
	z-index: var(--z-formbody-content-overlay);
	width: var(--px-500);
	margin-top: var(--px-24);
	margin-right: var(--px-40);
	margin-left: auto;
	.four-forty-reminder-message {
		color: var(--toast--color) !important;
		.createRichTextElementSpanWrapper {
			color: var(--toast--color) !important;
		}
	}
`;
